import React, { useState, useEffect, useRef, useMemo } from "react";
import { accountsAPI, usersAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { useAuth } from "../contexts/AuthContext";
import { FileText, Settings, BarChart3, Plus, Edit, Trash2, X, Loader, Check,
         Download, IndianRupee, TrendingUp, Upload, RefreshCw } from "lucide-react";
import FormatPickerModal from "../components/FormatPickerModal";
import * as XLSX from "xlsx";

const CLASS_LIST = ["PP1","PP2","Class 1","Class 2","Class 3","Class 4","Class 5",
                    "Class 6","Class 7","Class 8","Class 9","Class 10","Class 11","Class 12"];
const PROGRAM_TYPES = ["MDM", "breakfast"];
const STATUS_COLORS = {
  draft: "bg-[#F4F3F0] text-[#5C5147]",
  submitted: "bg-blue-50 text-blue-700",
  approved: "bg-[#EDF2EE] text-[#637C64]",
  paid: "bg-[#EDF2EE] text-[#637C64]"
};

const TABS = [
  { id: "dashboard",  label: "Overview",           icon: BarChart3, subKey: "finance_overview"   },
  { id: "statements", label: "Billing Statements", icon: FileText,  subKey: "finance_statements" },
  { id: "deo-indent", label: "DEO Indent Import",  icon: Upload,    subKey: "finance_deo_import" },
  { id: "rates",      label: "Billing Rates",      icon: Settings,  subKey: "finance_rates"      },
];

function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-4xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto animate-fade-in-up`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function exportStatementExcel(stmt) {
  const wb = XLSX.utils.book_new();
  const rows = [
    ["MANNA TRUST OPERATIONS — BILLING STATEMENT"],
    [`Period: ${stmt.period}  |  Program: ${stmt.program_type?.toUpperCase()}  |  Kitchen: ${stmt.kitchen_id || "All"}`],
    [`Generated: ${new Date(stmt.generated_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}`],
    [],
    ["School Name", "Route", ...CLASS_LIST.map(c => `${c} Students`), ...CLASS_LIST.map(c => `${c} Days`), "Total (₹)"],
    ...(stmt.schools || []).map(s => [
      s.school_name, s.route_number,
      ...CLASS_LIST.map(c => (s.line_items || []).find(li => li.class === c)?.avg_students_per_day || 0),
      ...CLASS_LIST.map(c => (s.line_items || []).find(li => li.class === c)?.days_served || 0),
      s.school_total
    ]),
    [],
    ["", "", ...Array(CLASS_LIST.length * 2 - 1).fill(""), "TOTAL", stmt.grand_total]
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 40 }, { wch: 12 }, ...CLASS_LIST.map(() => ({ wch: 10 })), ...CLASS_LIST.map(() => ({ wch: 8 })), { wch: 12 }];
  XLSX.utils.book_append_sheet(wb, ws, "Billing Statement");
  XLSX.writeFile(wb, `Billing_${stmt.period}_${stmt.program_type}.xlsx`);
}

export default function Accounts() {
  const { kitchens: ctxKitchens, selectedKitchen, changeKitchen } = useKitchen();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);

  // Keep local kitchens in sync with context
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) setKitchens(ctxKitchens);
  }, [ctxKitchens]);

  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  // Fallback: if active tab is hidden by permissions, switch to first visible tab
  useEffect(() => {
    const visible = TABS.filter(t => !t.subKey || user?.is_admin || user?.permissions?.[`sub_${t.subKey}_view`] !== false);
    if (visible.length > 0 && !visible.find(t => t.id === activeTab)) setActiveTab(visible[0].id);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const [dashboard, setDashboard] = useState(null);
  const [statements, setStatements] = useState([]);
  const [rates, setRates] = useState([]);
  const [selectedStmt, setSelectedStmt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [genMonth, setGenMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
  });
  const [genProgram, setGenProgram] = useState("MDM");
  const [showFormatPicker, setShowFormatPicker] = useState(false);
  const [pickerStmt, setPickerStmt] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [deoRecords, setDeoRecords] = useState([]);
  const [deoImporting, setDeoImporting] = useState(false);
  const [deoMsg, setDeoMsg] = useState("");
  const [deoMonth, setDeoMonth] = useState(() => new Date().toISOString().slice(0, 7));
  // Memoized DEO totals to avoid recomputing on every render
  const deoTotals = useMemo(() => ({
    primary: deoRecords.reduce((s, r) => s + (r.primary_enrollment || 0), 0),
    upper: deoRecords.reduce((s, r) => s + (r.upper_enrollment || 0), 0),
  }), [deoRecords]);

  const isAdmin = user?.is_admin;

  // Local kitchen selection for billing (independent of global selector)
  const [billingKitchen, setBillingKitchen] = useState(() =>
    localStorage.getItem("manna_selected_kitchen") || ""
  );
  // Sync billing kitchen when global changes (if not yet set)
  useEffect(() => {
    if (selectedKitchen && !billingKitchen) setBillingKitchen(selectedKitchen);
  }, [selectedKitchen]);

  // Fallback: direct fetch if context kitchens still empty after 1s
  useEffect(() => {
    const t = setTimeout(() => {
      if (kitchens.length > 0) return;
      usersAPI.getKitchens().then(r => {
        const ks = r.data || [];
        if (ks.length > 0) {
          setKitchens(ks);
          if (!billingKitchen) setBillingKitchen(ks[0].id);
        }
      }).catch(() => {});
    }, 1000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (selectedKitchen || activeTab) fetchTabData();
  }, [activeTab, selectedKitchen]);

  const fetchTabData = async () => {
    setLoading(true);
    try {
      if (activeTab === "dashboard") {
        const r = await accountsAPI.getDashboard({ kitchen_id: selectedKitchen || null });
        setDashboard(r.data);
      } else if (activeTab === "statements") {
        const r = await accountsAPI.getStatements({ kitchen_id: selectedKitchen || null });
        setStatements(r.data || []);
      } else if (activeTab === "rates") {
        const r = await accountsAPI.getBillingRates({});
        setRates(r.data || []);
      } else if (activeTab === "deo-indent") {
        const [year, month] = deoMonth.split("-").map(Number);
        const r = await accountsAPI.getDEOIndent({ month, year, kitchen_id: selectedKitchen || null });
        setDeoRecords(r.data?.records || []);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleGenerateBilling = async () => {
    if (!billingKitchen) { setError("Please select a kitchen to generate billing"); return; }
    setGenerating(true); setError("");
    const [year, month] = genMonth.split("-").map(Number);
    try {
      const r = await accountsAPI.generateBilling({ month, year, kitchen_id: billingKitchen });
      setModal(null);
      setSelectedStmt(r.data);
      setModal("view-statement");
      fetchTabData();
      setSuccess("Billing generated!"); setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(formatError(e)); }
    setGenerating(false);
  };

  const handleDEOImport = async (file) => {
    if (!file) return;
    setDeoImporting(true); setDeoMsg("");
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const [year, month] = deoMonth.split("-").map(Number);
        const r = await accountsAPI.importDEOIndent({
          month, year,
          file_base64: e.target.result,
          filename: file.name,
          kitchen_id: selectedKitchen || null,
        });
        setDeoMsg(`Imported ${r.data.school_count} schools for ${deoMonth} — Primary: ${r.data.total_primary}, Upper: ${r.data.total_upper}`);
        fetchTabData();
      } catch (e2) { setDeoMsg(`Error: ${formatError(e2)}`); }
      setDeoImporting(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDownloadVoucher = async (stmt) => {
    const { url, token } = accountsAPI.exportVoucherUrl(stmt.period, stmt.kitchen_id, null);
    try {
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) { setError("Failed to download voucher. Generate billing first."); return; }
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Mannatrust_Voucher_${stmt.period}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(formatError(e)); }
  };

  const handleDownloadBilling = async (stmt) => {
    const { url, token } = accountsAPI.exportBillingUrl(stmt.period, stmt.kitchen_id);
    try {
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) { setError("Failed to download. Generate billing first."); return; }
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Mannatrust_Billing_${stmt.period}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(formatError(e)); }
  };

  const handleSaveRate = async () => {
    setError("");
    try {
      if (modal.mode === "create") await accountsAPI.createBillingRate(form);
      else await accountsAPI.updateBillingRate(modal.id, form);
      setModal(null);
      fetchTabData();
    } catch (e) { setError(formatError(e)); }
  };

  const handleUpdateStatus = async (stmtId, status) => {
    await accountsAPI.updateStatementStatus(stmtId, status);
    fetchTabData();
    if (selectedStmt?.id === stmtId) setSelectedStmt({ ...selectedStmt, status });
  };

  const viewStatement = async (stmt) => {
    const r = await accountsAPI.getStatement(stmt.id);
    setSelectedStmt(r.data);
    setModal("view-statement");
  };

  const ratesByProgram = (program) => rates.filter(r => r.program_type === program);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="accounts-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Accounts & Billing</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">Government billing statements · Per-child, per-class rates</p>
        </div>
      </div>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64]">{success}</div>}
      {error && <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-lg px-4 py-2.5 text-sm text-[#C2624E]">{error}</div>}

      <div className="flex border-b border-[#E5E1DB]">
        {TABS.filter(t => !t.subKey || user?.is_admin || user?.permissions?.[`sub_${t.subKey}_view`] !== false).map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold flex items-center gap-2 transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div> : (
        <>
          {/* ── DASHBOARD ── */}
          {activeTab === "dashboard" && dashboard && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "This Month (₹)", value: `₹${(dashboard.this_month_total || 0).toLocaleString("en-IN", {maximumFractionDigits:0})}`, sub: `${dashboard.this_month_schools} schools · ${dashboard.this_month_status}`, color: "text-[#E8956D]" },
                  { label: "Total Billed YTD", value: `₹${(dashboard.total_billed_ytd || 0).toLocaleString("en-IN", {maximumFractionDigits:0})}`, sub: "This year", color: "text-[#5C5147]" },
                  { label: "Pending Payment", value: `₹${(dashboard.pending_payment || 0).toLocaleString("en-IN", {maximumFractionDigits:0})}`, sub: "From govt", color: "text-[#D98032]" },
                  { label: "Today's Strength", value: dashboard.today_strength_entries || 0, sub: "Schools entered today", color: "text-[#637C64]" },
                ].map(s => (
                  <div key={s.label} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                    <p className={`text-2xl font-extrabold font-[Manrope] ${s.color}`}>{s.value}</p>
                    <p className="text-xs font-bold text-[#3A322C] mt-1">{s.label}</p>
                    <p className="text-xs text-[#8A7E73]">{s.sub}</p>
                  </div>
                ))}
              </div>

              {/* Generate billing */}
              <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5">
                <h3 className="font-bold text-[#3A322C] mb-1">Generate Billing Statement</h3>
                <p className="text-xs text-[#8A7E73] mb-4">Select kitchen + month + program → auto-calculates school strength × rates × days served</p>
                <div className="flex gap-4 flex-wrap items-end">
                  {/* Kitchen selector — required for billing */}
                  <div className="flex-1 min-w-48">
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen *</label>
                    <select value={billingKitchen} onChange={e => setBillingKitchen(e.target.value)}
                      className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] ${!billingKitchen ? "border-[#C2624E] bg-[#FBEAE7]/20" : "border-[#E5E1DB]"}`}>
                      <option value="">— Select Kitchen —</option>
                      {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Month</label>
                    <input type="month" value={genMonth} onChange={e => setGenMonth(e.target.value)}
                      className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Program</label>
                    <select value={genProgram} onChange={e => setGenProgram(e.target.value)}
                      className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
                      {PROGRAM_TYPES.map(p => <option key={p} value={p} className="capitalize">{p === "MDM" ? "Mid-Day Meal (Lunch)" : "Breakfast"}</option>)}
                    </select>
                  </div>
                  <button onClick={handleGenerateBilling} disabled={generating || !billingKitchen}
                    className="bg-[#5C5147] text-white rounded-xl px-6 py-2.5 font-bold text-sm flex items-center gap-2 hover:bg-[#4A4038] disabled:opacity-60">
                    {generating ? <Loader size={15} className="animate-spin" /> : <IndianRupee size={15} />}
                    Generate Bill
                  </button>
                </div>
                {!billingKitchen && (
                  <p className="text-xs text-[#C2624E] mt-2">Select a kitchen to generate billing — each kitchen has its own government invoice</p>
                )}
              </div>

              {/* Recent statements */}
              {dashboard.recent_statements?.length > 0 && (
                <div>
                  <h3 className="font-bold text-[#3A322C] mb-3">Recent Statements</h3>
                  <div className="space-y-2">
                    {dashboard.recent_statements.map(s => (
                      <div key={s.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-[#FAFAF8]"
                        onClick={() => viewStatement(s)}>
                        <div>
                          <p className="font-bold text-[#3A322C]">{s.period}</p>
                          <p className="text-xs text-[#8A7E73]">₹{(s.total || 0).toLocaleString("en-IN", {maximumFractionDigits:0})}</p>
                        </div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[s.status] || "bg-gray-100 text-gray-600"}`}>{s.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STATEMENTS LIST ── */}
          {activeTab === "statements" && (
            <div className="space-y-4">
              <div className="flex justify-end gap-2">
                <button onClick={() => { setGenMonth(`${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,"0")}`); setModal("generate"); }}
                  className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1.5">
                  <Plus size={14} /> Generate New
                </button>
              </div>
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Period", "Program", "Schools", "Total Amount", "Status", "Actions"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {statements.map(s => (
                      <tr key={s.id} className="hover:bg-[#FAFAF8]">
                        <td className="px-4 py-3 font-bold text-[#3A322C]">{s.period}</td>
                        <td className="px-4 py-3 capitalize text-[#8A7E73]">{s.program_type}</td>
                        <td className="px-4 py-3 text-[#5C5147]">{s.school_count}</td>
                        <td className="px-4 py-3 font-bold text-[#E8956D]">₹{(s.grand_total || 0).toLocaleString("en-IN", {maximumFractionDigits:0})}</td>
                        <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[s.status]}`}>{s.status}</span></td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button onClick={() => viewStatement(s)} className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-2 py-1 rounded font-bold">View</button>
                            {s.status === "draft" && <button onClick={() => handleUpdateStatus(s.id, "submitted")} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-bold">Submit</button>}
                            {s.status === "submitted" && isAdmin && <button onClick={() => handleUpdateStatus(s.id, "approved")} className="text-xs bg-[#EDF2EE] text-[#637C64] px-2 py-1 rounded font-bold">Approve</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {statements.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8A7E73]">No billing statements yet — generate your first one</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── BILLING RATES ── */}
          {activeTab === "rates" && (
            <div className="space-y-6">
              {!isAdmin && (
                <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-4 py-3 text-sm text-[#D98032]">
                  Billing rates can only be modified by administrators.
                </div>
              )}
              {PROGRAM_TYPES.map(program => (
                <div key={program}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-[#3A322C] capitalize">{program === "MDM" ? "Mid-Day Meal (MDM)" : "Breakfast Program"} — Rates</h3>
                    {isAdmin && (
                      <button onClick={() => { setForm({ program_type: program, valid_from: new Date().toISOString().split("T")[0], valid_to: "2099-12-31" }); setModal({ type: "rate", mode: "create" }); setError(""); }}
                        className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-3 py-1.5 rounded-lg font-bold hover:bg-[#E8956D]/20">
                        + Add Rate
                      </button>
                    )}
                  </div>
                  <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F3F0]">
                        <tr>{["Class", "Rate/Child/Day (₹)", "Valid From", "Valid To", ""].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody className="divide-y divide-[#F4F3F0]">
                        {ratesByProgram(program).map(r => (
                          <tr key={r.id} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 font-medium text-[#3A322C]">{r.class_name}</td>
                            <td className="px-4 py-3 font-bold text-[#E8956D]">₹{r.rate_per_child_per_day}</td>
                            <td className="px-4 py-3 text-[#8A7E73]">{r.valid_from}</td>
                            <td className="px-4 py-3 text-[#8A7E73]">{r.valid_to === "2099-12-31" ? "Ongoing" : r.valid_to}</td>
                            <td className="px-4 py-3">
                              {isAdmin && (
                                <button onClick={() => { setForm({ ...r }); setModal({ type: "rate", mode: "edit", id: r.id }); setError(""); }} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={14} /></button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {ratesByProgram(program).length === 0 && <tr><td colSpan={5} className="text-center py-6 text-[#8A7E73]">No rates set</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* DEO INDENT IMPORT TAB */}
      {activeTab === "deo-indent" && (
        <div className="space-y-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-bold text-[#3A322C]">DEO Monthly Indent Import</h2>
              <p className="text-sm text-[#8A7E73] mt-0.5">Upload the monthly enrollment baseline received from the District Education Office</p>
            </div>
          </div>

          <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Month *</label>
                <input type="month" value={deoMonth} onChange={e => setDeoMonth(e.target.value)}
                  className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
              </div>
              <div>
                <button onClick={() => deoFileRef.current?.click()} disabled={deoImporting}
                  className="w-full flex items-center justify-center gap-2 bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-60">
                  {deoImporting ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                  {deoImporting ? "Importing..." : "Upload DEO Excel"}
                </button>
                <input ref={deoFileRef} type="file" accept=".xls,.xlsx" className="hidden"
                  onChange={e => { if (e.target.files[0]) handleDEOImport(e.target.files[0]); e.target.value = ""; }} />
              </div>
            </div>

            {deoMsg && (
              <div className={`rounded-lg px-4 py-3 text-sm ${deoMsg.startsWith("Error") ? "bg-[#FBEAE7] text-[#C2624E]" : "bg-[#EDF2EE] text-[#637C64]"}`}>
                {deoMsg}
              </div>
            )}

            <div className="bg-[#F4F3F0] rounded-xl p-3 text-xs text-[#5C5147] space-y-1">
              <p className="font-bold text-[#3A322C]">Expected Excel format:</p>
              <p>Columns: School Code | School Name | Mandal | Primary Enrollment (1-5) | Upper Enrollment (6-10)</p>
              <p className="text-[#8A7E73]">Headers must be in row 1. The system will auto-match your column names.</p>
            </div>
          </div>

          {deoRecords.length > 0 && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB] flex items-center justify-between">
                <p className="font-bold text-[#3A322C] text-sm">{deoRecords.length} schools imported for {deoMonth}</p>
                <div className="flex gap-3 text-xs">
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
                    Primary: {deoTotals.primary.toLocaleString()}
                  </span>
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">
                    Upper: {deoTotals.upper.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-xs">
                  <thead className="bg-[#F8F8F6] sticky top-0">
                    <tr>{["District","Mandal","School Code","School Name","Route","Primary (1-5)","Upper (6-10)","Total"].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 font-bold uppercase text-[#5C5147] whitespace-nowrap">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {deoRecords.map((r, i) => (
                      <tr key={`item-${i}`} className="hover:bg-[#FAFAF8]">
                        <td className="px-3 py-2 text-[#8A7E73]">{r.district || "—"}</td>
                        <td className="px-3 py-2 text-[#5C5147]">{r.mandal}</td>
                        <td className="px-3 py-2 font-mono text-[#8A7E73]">{r.school_code || "—"}</td>
                        <td className="px-3 py-2 font-medium text-[#3A322C] max-w-[200px] truncate">{r.school_name}</td>
                        <td className="px-3 py-2"><span className="text-[#E8956D] font-bold">{r.route_number || "—"}</span></td>
                        <td className="px-3 py-2 font-bold text-green-700">{(r.primary_enrollment || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 font-bold text-blue-700">{(r.upper_enrollment || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 font-extrabold text-[#3A322C]">{(r.total_enrollment || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* RATE MODAL */}
      {modal?.type === "rate" && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} Billing Rate`} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Class *</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.class_name || ""} onChange={e => setForm({ ...form, class_name: e.target.value })}>
                <option value="">Select class</option>
                {CLASS_LIST.map(c => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Program Type</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.program_type || "MDM"} onChange={e => setForm({ ...form, program_type: e.target.value })}>
                {PROGRAM_TYPES.map(p => <option key={p} value={p} className="capitalize">{p === "MDM" ? "Mid-Day Meal" : "Breakfast"}</option>)}
              </select></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Rate per Child per Day (₹) *</label>
              <input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.rate_per_child_per_day || ""} onChange={e => setForm({ ...form, rate_per_child_per_day: parseFloat(e.target.value) })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Valid From</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.valid_from || ""} onChange={e => setForm({ ...form, valid_from: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Valid To</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.valid_to === "2099-12-31" ? "" : form.valid_to || ""} onChange={e => setForm({ ...form, valid_to: e.target.value || "2099-12-31" })} placeholder="Leave blank for ongoing" /></div>
            </div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSaveRate} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save Rate</button>
            </div>
          </div>
        </Modal>
      )}


      {/* GENERATE BILLING MODAL */}
      {modal === "generate" && (
        <Modal title="Generate Billing Statement" onClose={() => { setModal(null); setError(""); }}>
          <div className="space-y-4">
            <p className="text-sm text-[#8A7E73]">Select kitchen and month to generate a new billing statement using school strength data.</p>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen *</label>
              <select value={billingKitchen} onChange={e => setBillingKitchen(e.target.value)}
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] ${!billingKitchen ? "border-[#C2624E]" : "border-[#E5E1DB]"}`}>
                <option value="">— Select Kitchen —</option>
                {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
              {!billingKitchen && <p className="text-xs text-[#C2624E] mt-1">A kitchen must be selected — each kitchen has its own government invoice</p>}
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Month *</label>
              <input type="month" value={genMonth} onChange={e => setGenMonth(e.target.value)}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" />
            </div>
            {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-lg px-3 py-2 text-sm">{error}</div>}
            <div className="flex gap-3 pt-2">
              <button onClick={() => { setModal(null); setError(""); }} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleGenerateBilling} disabled={generating || !billingKitchen}
                className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-xl py-2.5 font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#D48661] disabled:opacity-60">
                {generating ? <Loader size={15} className="animate-spin" /> : <IndianRupee size={15} />}
                {generating ? "Generating..." : "Generate Billing"}
              </button>
            </div>
          </div>
        </Modal>
      )}


      {/* VIEW STATEMENT MODAL */}
      {modal === "view-statement" && selectedStmt && (
        <Modal title={`Billing Statement — ${selectedStmt.period} (${selectedStmt.program_type?.toUpperCase()})`} onClose={() => setModal(null)} wide>
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex gap-3">
                <div className="text-center px-4 py-2 bg-[#F4F3F0] rounded-xl">
                  <p className="text-xl font-extrabold text-[#E8956D]">₹{(selectedStmt.grand_total || 0).toLocaleString("en-IN", {maximumFractionDigits:0})}</p>
                  <p className="text-xs text-[#8A7E73]">Grand Total</p>
                </div>
                <div className="text-center px-4 py-2 bg-[#F4F3F0] rounded-xl">
                  <p className="text-xl font-extrabold text-[#5C5147]">{selectedStmt.school_count}</p>
                  <p className="text-xs text-[#8A7E73]">Schools</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => { setPickerStmt(selectedStmt); setShowFormatPicker(true); }}
                  className="flex items-center gap-1.5 text-sm bg-[#2D5A27] text-white rounded-lg px-4 py-2 font-bold hover:bg-[#1e3e1a]">
                  <Download size={14} /> Download Report
                </button>
                {selectedStmt.status === "draft" && (
                  <button onClick={() => { handleUpdateStatus(selectedStmt.id, "submitted"); setModal(null); }}
                    className="flex items-center gap-1.5 text-sm bg-blue-600 text-white rounded-lg px-4 py-2 font-bold">Submit to Govt</button>
                )}
              </div>
            </div>
            {/* School-wise table */}
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#F4F3F0] sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold uppercase text-[#5C5147]">School</th>
                    <th className="text-left px-3 py-2 font-bold uppercase text-[#5C5147]">Route</th>
                    {["PP1","PP2","Cl 1-5","Cl 6-10","Cl 11-12"].map(h => (
                      <th key={h} className="text-right px-2 py-2 font-bold uppercase text-[#5C5147]">{h}</th>
                    ))}
                    <th className="text-right px-3 py-2 font-bold uppercase text-[#E8956D]">Total (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F4F3F0]">
                  {(selectedStmt.schools || []).slice(0, 100).map((s, i) => {
                    const getAmt = (classes) => classes.reduce((sum, c) => sum + ((s.line_items || []).find(li => li.class === c)?.amount || 0), 0);
                    return (
                      <tr key={`idx-${i}`} className="hover:bg-[#FAFAF8]">
                        <td className="px-3 py-2 font-medium text-[#3A322C] max-w-48 truncate">{s.school_name}</td>
                        <td className="px-3 py-2 text-[#8A7E73]">{s.route_number}</td>
                        <td className="px-2 py-2 text-right">{getAmt(["PP1"]).toFixed(0)}</td>
                        <td className="px-2 py-2 text-right">{getAmt(["PP2"]).toFixed(0)}</td>
                        <td className="px-2 py-2 text-right">{getAmt(["Class 1","Class 2","Class 3","Class 4","Class 5"]).toFixed(0)}</td>
                        <td className="px-2 py-2 text-right">{getAmt(["Class 6","Class 7","Class 8","Class 9","Class 10"]).toFixed(0)}</td>
                        <td className="px-2 py-2 text-right">{getAmt(["Class 11","Class 12"]).toFixed(0)}</td>
                        <td className="px-3 py-2 text-right font-bold text-[#E8956D]">₹{(s.school_total || 0).toFixed(0)}</td>
                      </tr>
                    );
                  })}
                  {(selectedStmt.schools || []).length > 100 && (
                    <tr><td colSpan={8} className="text-center py-2 text-xs text-[#8A7E73]">Showing 100 of {selectedStmt.schools.length} schools — Export Excel for full data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      )}

      {/* FORMAT PICKER — shown when "Download Report" is clicked */}
      {showFormatPicker && pickerStmt && (
        <FormatPickerModal
          reportType="billing"
          params={{
            month:      pickerStmt.month,
            year:       pickerStmt.year,
            period:     pickerStmt.period,
            kitchen_id: pickerStmt.kitchen_id,
          }}
          title={`Download Billing — ${pickerStmt.period}`}
          onClose={() => { setShowFormatPicker(false); setPickerStmt(null); }}
        />
      )}
    </div>
  );
}
